import { getFileSize } from '@/src/util/files';

import {
  geInboxEntryAllowClose,
  geInboxEntryPermissions,
  mapInboxEntryWorkstreams,
} from './InboxEntry';

import {
  FileEntry,
  FileEntryPermission,
  FileEntryToUpdateStatus,
  InboxEntryStatus,
} from '../types';

export const getFileEntryPermissions = (rawPermissions: string[]): FileEntryPermission => {
  const permissions = geInboxEntryPermissions(rawPermissions);

  return {
    trash: permissions['trash'] || false,
    deletePermanent: permissions['permanent_delete'] || false,
    misc: permissions['misc'] || false,
    tagWorkstream: permissions['tag_wokstream'] || false,
    restore: permissions['restore'] || false,
    record: permissions['record'] || false,
    rename: permissions['rename'] || false,
    duplicate: permissions['duplicate'] || false,
    changeStatus: permissions['change-status'] || false,
    createTask: permissions['create_task'] || false,
    archive: permissions['archive'] || false,
    copyFileUrl: permissions['copy_url'] || false,
    download: permissions['download'] || false,
    read: permissions['read'] || false,
    createTransaction: permissions['create_transaction'] || false,
  };
};

export const mapFileEntry = (rawFileEntry): FileEntry => {
  return {
    id: rawFileEntry.id,
    file: {
      id: rawFileEntry.document.id,
      name: rawFileEntry.document['file-origin'],
      rawName: rawFileEntry.document['file-name'],
      user: {
        id: rawFileEntry.created.user.id,
        firstName: rawFileEntry.created.user.firstName,
        lastName: rawFileEntry.created.user.lastName,
      },
      folder: {
        id: rawFileEntry.document.folderId,
        location: rawFileEntry.document['file-location'],
      },
      googleDriveFileUrl: rawFileEntry['google-drive-file-url'],
      isFileSupported: !rawFileEntry.document['file-name'].startsWith('NOT_UPLOADED_'),
      libraryLocation: {
        id: rawFileEntry['library-location']?.id,
        path: rawFileEntry['library-location']?.['full-path'],
        tempBreadCrumbs: rawFileEntry['library-location']?.['tempBreadCrumbs'],
      },
    },
    source: rawFileEntry.source,
    status: rawFileEntry.status,
    from: {
      id: rawFileEntry.from.id,
      emailAddress: '',
      fullName: `${rawFileEntry.created.user.firstName} ${rawFileEntry.created.user.lastName}`,
    },
    createdDate: rawFileEntry.created.date,
    openedDate: rawFileEntry.opened?.date,
    modifiedBy: rawFileEntry.modified?.user
      ? `${rawFileEntry.modified.user.firstName} ${rawFileEntry.modified.user.lastName}`
      : null,
    modifiedDate: rawFileEntry.modified?.date,
    openedBy: rawFileEntry.opened?.user
      ? `${rawFileEntry.opened.user.firstName} ${rawFileEntry.opened.user.lastName}`
      : null,
    closedDate: rawFileEntry.closed?.date,
    closedBy: rawFileEntry.closed?.user
      ? `${rawFileEntry.closed.user.firstName} ${rawFileEntry.closed.user.lastName}`
      : null,
    workstreams: mapInboxEntryWorkstreams(rawFileEntry.workstreams),
    tasks: rawFileEntry.tasks,
    fileLocation: rawFileEntry.document['file-location'],
    fileSize: getFileSize(rawFileEntry.document['file-size-bytes']),
    accessList: rawFileEntry['access-list'],
    isMisc: rawFileEntry.misc,
    isRecorded: !!rawFileEntry.document.recorded,
    recordedDate: rawFileEntry.document.recorded?.date,
    recordedBy: rawFileEntry.document.recorded?.name,
    isRead: rawFileEntry.read,
    isArchivedInLibrary: rawFileEntry.document.archived,
    isDeleted: rawFileEntry.trash === true,
    allowedStatusChanges: rawFileEntry['allowed-status-changes'] || [],
    allowClose: geInboxEntryAllowClose(rawFileEntry['allow-close']),
    permissions: getFileEntryPermissions(rawFileEntry.permissions),
    shouldWaitSocketUpdate: rawFileEntry.shouldWaitSocketUpdate,
    isMultiPagePdf: rawFileEntry['is-multi-page-pdf'],
    duplicate: rawFileEntry['duplicate'],
    tags: rawFileEntry.tags || [],
  };
};

export const getFileEntryToUpdateStatus = (fileEntry: FileEntry): FileEntryToUpdateStatus => {
  return {
    id: fileEntry.file.id,
    name: fileEntry.file.name,
    status: fileEntry.status,
    isFileSupported: fileEntry.file.isFileSupported,
    // isMisc: fileEntry.isMisc,
    // isRecorded: fileEntry.isRecorded,
    isRead: fileEntry.isRead,
    // isArchivedInLibrary: fileEntry.isArchivedInLibrary,
    // tasks: fileEntry.tasks || [],
    allowedStatusChanges: fileEntry.allowedStatusChanges,
    allowClose: fileEntry.allowClose,
    duplicate: fileEntry.duplicate,
  };
};

type FileEntryRequiredProps = {
  isMisc: boolean;
  isRecorded: boolean;
  isArchivedInLibrary: boolean;
  tasks: any[];
  status: InboxEntryStatus;
};

export const checkFileHasRequiredAction = (fileEntry: FileEntryRequiredProps) => {
  if (fileEntry.isMisc) {
    return false;
  }

  const hasTasks = fileEntry.tasks.length > 0;
  const isRecorded = !!fileEntry.isRecorded;

  return fileEntry.status !== InboxEntryStatus.Closed
    ? !(fileEntry.isArchivedInLibrary || hasTasks || isRecorded)
    : false;
};
